import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getTranslation, SUPPORTED_LANGUAGES } from '../translations';
import { warmFromPayloads } from '../utils/imageWarm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Determine game mode from URL params (?mode=dev means dev bot)
const urlParams = new URLSearchParams(window.location.search);
const GAME_MODE = urlParams.get('mode') === 'dev' ? 'dev' : 'release';

// Create axios instance with game mode header
const api = axios.create({
  baseURL: API,
  timeout: 10000,
  headers: {
    'X-Game-Mode': GAME_MODE,
  },
});

// Context
const GameContext = createContext(null);

// ---- External live-coin store ----
// The per-second exhibit drip used to be React state on the context,
// which re-rendered EVERY useGame() consumer (the whole gallery grid,
// 50+ tiles) once per second — the main cause of tap lag for players
// with big collections. The store ticks OUTSIDE React; only the tiny
// components that call useLiveCoins()/useCoinFlash() re-render.
const coinStore = {
  listeners: new Set(),
  liveCoins: 0,
  flash: { seq: 0, amount: 0, haptic: true },
  notify() { this.listeners.forEach((l) => { try { l(); } catch (_) {} }); },
  setCoins(v) { if (v !== this.liveCoins) { this.liveCoins = v; this.notify(); } },
  emitFlash(amount, hapticFlag) {
    this.flash = { seq: (this.flash.seq + 1) | 0, amount, haptic: hapticFlag };
    this.notify();
  },
};
const coinSubscribe = (l) => { coinStore.listeners.add(l); return () => coinStore.listeners.delete(l); };
export const getLiveCoins = () => coinStore.liveCoins;
export const useLiveCoins = () => useSyncExternalStore(coinSubscribe, getLiveCoins);
export const useCoinFlash = () => useSyncExternalStore(coinSubscribe, () => coinStore.flash);

export const GameProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(true);
  // Splash progress (0-100) + current boot stage, driven by the real
  // boot steps: auth → prefetch responses → ready.
  const [bootProgress, setBootProgress] = useState(4);
  const [bootStage, setBootStage] = useState('connecting');
  const [error, setError] = useState(null);
  const [tutorialStepRaw, setTutorialStepRaw] = useState(null);
  // Persist the active tutorial step so a player who quits mid-tutorial
  // returns to the exact step on next open. Keyed by user id so two
  // accounts on one device don't bleed state. `null` means tutorial
  // finished or not started.
  //
  // Reset policy: if the player owns < 2 paintings when they reopen
  // the app, we restart the tutorial from `details` regardless of the
  // saved step. The tutorial's whole purpose is to lead to that second
  // painting, so respecting a half-way step that lives on a non-current
  // screen would just leave the player lost on the home view with no
  // visible prompts. Once they own ≥ 2 cards, we trust them to find
  // their own way and let the saved step (if any) keep highlighting.
  const TUTORIAL_LS_KEY = (userId) => `pa_tutorial_step:${userId || 'anon'}`;

  // Boot-time prefetch cache — populated inside `initUser` once the
  // auth token is set, and read once-per-mount by EarnPage and
  // GalleryPage to skip their first network round-trip. Ref (not
  // state) so a write inside the auth effect doesn't trigger a
  // re-render of every consumer of the context value.
  const prefetchCacheRef = useRef({});
  const consumePrefetch = useCallback((key) => {
    const val = prefetchCacheRef.current[key];
    if (val !== undefined) {
      // Single-use: clear so a remount (e.g. tab switch back) hits
      // fresh data, not a 30s-old snapshot.
      delete prefetchCacheRef.current[key];
      return val;
    }
    return null;
  }, []);
  const peekPrefetch = useCallback((key) => {
    // Same lookup as `consumePrefetch` but doesn't delete — for callers
    // (e.g. AppContent.checkOnboarding) that only need to inspect the
    // payload and want to leave it intact for the eventual page mount.
    const val = prefetchCacheRef.current[key];
    return val !== undefined ? val : null;
  }, []);
  const setTutorialStep = useCallback((next) => {
    setTutorialStepRaw(next);
    try {
      const uid = user?.id || null;
      const k = TUTORIAL_LS_KEY(uid);
      if (next) localStorage.setItem(k, next);
      else localStorage.removeItem(k);
    } catch { /* ignore quota/private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  // Restore / reset tutorial step on user load.
  useEffect(() => {
    if (!user?.id) return;
    const cardsCount = user?.cards_count || 0;
    try {
      const k = TUTORIAL_LS_KEY(user.id);
      if (cardsCount < 2) {
        // Fewer than 2 paintings means the player hasn't completed the
        // critical "buy your second painting" milestone yet — restart
        // the flow from the start so all prompts are visible again,
        // and overwrite whatever step was cached.
        // Tutorial starts at the repair prompt — the detail modal opens
        // automatically with the freshly claimed painting, so there is
        // no separate "open the details" step any more.
        const fresh = 'repair';
        if (tutorialStepRaw !== fresh) setTutorialStepRaw(fresh);
        localStorage.setItem(k, fresh);
      } else {
        // Returning player past the milestone — keep whatever step
        // they had cached (or nothing).
        const saved = localStorage.getItem(k);
        if (saved && saved !== tutorialStepRaw) setTutorialStepRaw(saved);
      }
    } catch { /* ignore */ }
    // We intentionally only re-evaluate on user id / cards_count change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.cards_count]);
  const tutorialStep = tutorialStepRaw;
  // Tutorial steps:
  // 'repair' → 'exhibit' → 'earn' → 
  // 'discover_claim' → 'upgrades' → 'upgrade_slots' → 'gallery' → 'buy_painting'

  const t = useCallback((key) => {
    return getTranslation(language, key);
  }, [language]);

  // Stop every CSS animation while the Mini App sits in the background —
  // Telegram keeps the WebView alive, so without this the phone keeps
  // compositing particles and glows it never shows.
  useEffect(() => {
    const sync = () => {
      document.body.classList.toggle('app-hidden', document.visibilityState === 'hidden');
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      document.body.classList.remove('app-hidden');
    };
  }, []);

  const initUser = useCallback(async () => {
    setLoading(true);
    setError(null);
    setBootProgress(8);
    setBootStage('connecting');
    
    try {
      // Check if running in Telegram WebApp
      const tg = window.Telegram?.WebApp;
      
      if (tg?.initData) {
        // Authenticate via Telegram
        const urlParams = new URLSearchParams(window.location.search);
        const urlRef = urlParams.get('ref') || '';
        const startParam = tg.initDataUnsafe?.start_param || urlRef || '';
        const response = await api.post('/auth/telegram', {
          init_data: tg.initData,
          start_param: startParam
        });
        const { user: userData, token } = response.data;
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
        // Auto-detect language from Telegram or user preference
        const userLang = userData.language || 'en';
        const mappedLang = SUPPORTED_LANGUAGES.includes(userLang) ? userLang : 'en';
        setLanguage(mappedLang);
      } else {
        // Development mode - use dev auth
        const urlParams = new URLSearchParams(window.location.search);
        const urlRef = urlParams.get('ref') || '';
        const response = await api.post('/auth/dev', null, {
          params: { telegram_id: 123456789, username: 'dev_user', start_param: urlRef }
        });
        const { user: userData, token } = response.data;
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
      }

      // Boot-time prefetch — fire requests for the heaviest landing
      setBootProgress(40);
      setBootStage('gallery');
      // surfaces (Gallery + Earn) in parallel so that, by the time the
      // player taps a tab, the data is already in memory. EarnPage and
      // GalleryPage check `consumePrefetch(<endpoint>)` on mount and
      // skip their own network call if a hit is available. Loading
      // tips on the splash screen mask this extra second of waiting.
      // `Promise.allSettled` so a flaky single endpoint can't block
      // app launch — the failing page just falls back to its own fetch.
      const prefetchEndpoints = [
        '/cards/me',                  // GalleryPage primary feed
        '/cards/stats',               // GalleryPage stats + App.js onboarding check
        '/earn/me',                   // EarnPage primary feed
        '/daily-bonus/me',            // EarnPage daily bonus widget
        '/cards/preview-pool',        // GalleryPage roulette pool
        '/users/me/collection-value', // GalleryPage value pill
        '/upgrades/me',                  // UpgradesPage list + maxPaintings/maxExhibits
        '/gallery/top',                  // TopPage paintings tab
        '/gallery/top-nft',              // TopPage NFT tab
        '/leaderboard?limit=100&fresh=1', // TopPage players tab (force-fresh on app launch)
        '/leaderboard/me',               // TopPage "my rank" pill
        '/ton/info',                     // CardDetailsModal "Make TON Artwork" button price + network
      ];
      let done = 0;
      const settled = await Promise.allSettled(
        prefetchEndpoints.map((url) => api.get(url).finally(() => {
          done += 1;
          setBootProgress(40 + Math.round(55 * done / prefetchEndpoints.length));
        }))
      );
      settled.forEach((res, i) => {
        if (res.status === 'fulfilled') {
          prefetchCacheRef.current[prefetchEndpoints[i]] = res.value.data;
        }
      });
      setBootProgress(100);
      setBootStage('ready');
      // Pull every artwork we already know about into the browser cache
      // while the player looks at the gallery, so Discover / roulette /
      // auction / collections don't stall on first open.
      warmFromPayloads(Object.values(prefetchCacheRef.current));
      (async () => {
        for (const url of ['/collections/me', '/auction/state']) {
          try {
            const res = await api.get(url);
            warmFromPayloads([res.data]);
          } catch { /* best-effort warm-up */ }
        }
      })();
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await api.get('/users/me');
      setUser(response.data);
    } catch (err) {
      console.error('Refresh user error:', err);
    }
  }, [user?.id]);

  const changeLanguage = useCallback(async (newLang) => {
    setLanguage(newLang);
    if (user?.id) {
      try {
        await api.put('/users/me/language', null, {
          params: { language: newLang }
        });
      } catch (err) {
        console.error('Change language error:', err);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    initUser();
  }, [initUser]);

  // Global axios response interceptor — keep `user.coins` in sync with
  // whatever the backend just returned, regardless of which page made
  // the call. Every coin-touching endpoint (`/upgrades/me`,
  // `/cards/me`, `/users/me`, `/earn/...`, `/cards/buy-random`,
  // `/cards/{id}/repair`, …) returns either `{ coins: N }`,
  // `{ coins_remaining: N }`, or a full `UserResponse` with
  // `{ coins: N }`. We pluck the freshest value and push it into the
  // context so `liveCoins` (which is `user.coins + drip-since-sync`)
  // reads the same authoritative number on every screen.
  //
  // CRUCIAL: every successful coin sync also bumps `coinSyncAtRef` to
  // "now". The live-drip math only adds the share earned *since* that
  // timestamp, so we never double-credit any portion the server has
  // already paid into `user.coins`. This replaces the old
  // `exhibit_coins_paid`-based formula which double-counted whenever
  // the interceptor refreshed user.coins out-of-band with the card
  // payload.
  const coinSyncAtRef = useRef(Date.now());
  const userCoinsRef = useRef(0);
  const activeExhibitsRef = useRef([]);
  // Same integer math as the server's `apply_partial_exhibit_commit` —
  // floored on both sides so the client never shows a value the server
  // hasn't conceptually credited. Reads refs only → stable callback.
  const computeLiveCoins = useCallback(() => {
    const base = userCoinsRef.current || 0;
    const exhibits = activeExhibitsRef.current;
    if (exhibits.length === 0) return Math.floor(base);
    let pending = 0;
    const now = Date.now();
    const syncedAt = coinSyncAtRef.current;
    for (const c of exhibits) {
      if (!c.exhibit_ends || !c.exhibit_start) continue;
      const start = new Date(c.exhibit_start).getTime();
      const end = new Date(c.exhibit_ends).getTime();
      const total = end - start;
      if (total <= 0) continue;
      const expected = c.exhibit_expected_reward || 0;
      if (expected <= 0) continue;
      const elapsedAtSync = Math.max(0, Math.min(total, syncedAt - start));
      const elapsedNow = Math.max(0, Math.min(total, now - start));
      const paidAtSync = Math.floor((expected * elapsedAtSync) / total);
      const paidAtNow = Math.floor((expected * elapsedNow) / total);
      pending += Math.max(0, paidAtNow - paidAtSync);
    }
    return Math.floor(base + pending);
  }, []);
  useEffect(() => {
    const id = api.interceptors.response.use((response) => {
      try {
        const d = response?.data;
        if (!d || typeof d !== 'object') return response;
        // Skip /auth/* responses — they set the full user explicitly
        // and we don't want to fight that flow.
        const url = response?.config?.url || '';
        if (url.includes('/auth/')) return response;
        // Prefer the most specific signal first.
        let nextCoins = null;
        if (typeof d.coins_remaining === 'number') nextCoins = d.coins_remaining;
        else if (typeof d.coins === 'number') nextCoins = d.coins;
        else if (d.user && typeof d.user.coins === 'number') nextCoins = d.user.coins;
        if (nextCoins !== null) {
          // Reset the drip baseline regardless of whether the value
          // changed — even a same-value response means the server just
          // confirmed "this is the current authoritative balance at
          // this moment". The drip starts fresh from here.
          coinSyncAtRef.current = Date.now();
          userCoinsRef.current = nextCoins;
          coinStore.setCoins(computeLiveCoins());
          setUser((prev) => {
            if (!prev) return prev;
            if (prev.coins === nextCoins) return prev;
            return { ...prev, coins: nextCoins };
          });
        }
        // Also propagate other commonly-returned authoritative fields
        // so cards_count / total_rating / max_paintings stay in sync.
        if (d.user && typeof d.user === 'object' && d.user.id) {
          setUser((prev) => prev ? { ...prev, ...d.user } : prev);
        }
      } catch (_) { /* never let interception break a response */ }
      return response;
    });
    return () => api.interceptors.response.eject(id);
  }, []);

  // Show in-app notifications (e.g. admin grant) when user opens the app
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/users/me/notifications');
        if (cancelled) return;
        const unread = (res.data || []).filter(n => !n.read);
        if (unread.length === 0) return;

        for (const n of unread) {
          if (n.type === 'admin_grant') {
            const amount = n.amount || 0;
            const balance = n.balance_after || 0;
            if (amount > 0) {
              toast.success(
                language === 'ru'
                  ? `🎁 Начислено ${amount.toLocaleString()} монет от администратора! Баланс: ${balance.toLocaleString()}`
                  : `🎁 Admin granted you ${amount.toLocaleString()} coins! Balance: ${balance.toLocaleString()}`,
                { duration: 7000 }
              );
            } else {
              toast(
                language === 'ru'
                  ? `Списано ${Math.abs(amount).toLocaleString()} монет администратором.`
                  : `Admin deducted ${Math.abs(amount).toLocaleString()} coins.`,
                { duration: 6000 }
              );
            }
          } else if (n.type === 'auction_outbid') {
            const title = (n.title || '').replace(/^["'«»]+|["'«»]+$/g, '').trim();
            const newAmt = (n.new_bid || 0).toLocaleString();
            const newBidder = n.new_bidder_username ? `@${n.new_bidder_username}` : '';
            const isBuyout = n.kind === 'buyout';
            const msg = language === 'ru'
              ? `${isBuyout ? '⚡' : '🔔'} Вашу ставку перебили на «${title}» — ${newAmt} ⭐${newBidder ? ' от ' + newBidder : ''}`
              : `${isBuyout ? '⚡' : '🔔'} You've been outbid on "${title}" — ${newAmt} ⭐${newBidder ? ' by ' + newBidder : ''}`;
            toast(msg, { duration: 8000, icon: isBuyout ? '⚡' : '🔔' });
          }
        }
        // Mark them as read so we don't repeat next time
        await api.post('/users/me/notifications/read', {
          ids: unread.map(n => n.id)
        });
        // Refresh user balance
        try { await refreshUser(); } catch (_) {}
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Initialize Telegram WebApp
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
      tg.setHeaderColor('#05050A');
      tg.setBackgroundColor('#05050A');
      // Prevent swipe-down from minimizing/closing the Mini App (Bot API 7.7+)
      if (tg.disableVerticalSwipes) {
        tg.disableVerticalSwipes();
      }
    }
  }, []);

  const haptic = useCallback(() => {
    try {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    } catch (_) {}
  }, []);

  // Live coin balance: every screen (Earn, Upgrades, Shop, Profile, …)
  // shows the same number ticking up. GalleryPage actively publishes
  // exhibit data via `publishActiveExhibits` when it's mounted, but to
  // survive a *page refresh on any non-gallery route* (e.g. user does
  // F5 on /upgrades) the context itself ALSO fetches the active
  // exhibits once on auth and on every focus event. Without this
  // fallback, refreshing on /upgrades meant Gallery never mounted →
  // `activeExhibits` stayed empty → drip ticker froze.
  const [activeExhibits, setActiveExhibits] = useState([]);
  const publishActiveExhibits = useCallback((cards) => {
    if (!Array.isArray(cards)) {
      activeExhibitsRef.current = [];
      setActiveExhibits([]);
      return;
    }
    const now = Date.now();
    // Keep the minimal shape needed by `computeLiveCoins` so we
    // don't hold the full card payload (which contains image URLs and
    // other data) in context state.
    const slim = cards
      .filter(c => c?.exhibit_ends && new Date(c.exhibit_ends).getTime() > now - 60_000)
      .map(c => ({
        id: c.id,
        exhibit_start: c.exhibit_start,
        exhibit_ends: c.exhibit_ends,
        exhibit_expected_reward: c.exhibit_expected_reward || 0,
      }));
    activeExhibitsRef.current = slim;
    coinStore.setCoins(computeLiveCoins());
    setActiveExhibits(slim);
  }, [computeLiveCoins]);

  // Context-owned fallback fetcher — called once user.id is known so
  // the drip ticker works on any landing page (not just Gallery).
  // GalleryPage's mount-time `publishActiveExhibits` overrides this
  // with the fresher data; both sources are compatible because the
  // shape is identical.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const fetchExhibits = async () => {
      try {
        const res = await api.get('/cards/me');
        if (cancelled) return;
        publishActiveExhibits(res.data || []);
      } catch (_) { /* silent — gallery mount will retry */ }
    };
    fetchExhibits();
    // Refetch when the tab comes back to foreground so the drip
    // baseline is accurate after the device wakes up.
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchExhibits();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user?.id, publishActiveExhibits]);

  // 1Hz drip ticker — writes to the external coinStore only, so the
  // provider (and every useGame() consumer) does NOT re-render each
  // second. Paused while the Mini App tab is hidden to save battery.
  useEffect(() => {
    if (activeExhibits.length === 0) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      const prev = coinStore.liveCoins;
      const v = computeLiveCoins();
      // Auto-flash on whole-coin drip. Capped at delta<=50 so explicit
      // rewards (which call flashCoinReward with the precise amount)
      // don't double-fire. haptic:false keeps the device silent.
      if (v > prev && v - prev <= 50) coinStore.emitFlash(v - prev, false);
      coinStore.setCoins(v);
    }, 1000);
    return () => clearInterval(id);
  }, [activeExhibits.length, computeLiveCoins]);

  // Structural sync: whenever the authoritative balance changes (auth,
  // setUser from any page), refresh the ref + store immediately.
  useEffect(() => {
    userCoinsRef.current = user?.coins || 0;
    coinStore.setCoins(computeLiveCoins());
  }, [user?.coins, computeLiveCoins]);

  // Floating "+N" reward bus — any screen can call
  // `flashCoinReward(amount, { haptic })`; the LiveCoinPill components
  // subscribed to the store play the animation.
  const flashCoinReward = useCallback((amount, opts = {}) => {
    if (!amount || amount <= 0) return;
    coinStore.emitFlash(amount, opts.haptic !== false);
  }, []);

  const value = {
    user,
    setUser,
    language,
    setLanguage: changeLanguage,
    loading,
    bootProgress,
    bootStage,
    error,
    t,
    refreshUser,
    api,
    haptic,
    tutorialStep,
    setTutorialStep,
    publishActiveExhibits,
    flashCoinReward,
    consumePrefetch,
    peekPrefetch,
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export { api };
